import React, { useState } from "react";
import { Button, DropdownField, Modal } from "../../../components/ui";
import { ENQUIRY_STATUS_OPTIONS } from "../data/enquiryFormOptions";
import type { EnquiryStatus } from "../types/enquiries.types";

export interface ChangeStatusModalProps {
  /** Whose stage is being moved. Named in the dialog, so a menu opened on the
   *  wrong row is caught before the change lands. */
  name: string;
  /** Where the enquiry stands now — what the dialog opens on. */
  status: EnquiryStatus;
  onClose: () => void;
  /** Saves the stage. Closing the dialog after it is the caller's. */
  onSave: (status: EnquiryStatus) => void;
  /**
   * Set while the save is with the server: the button spins and stops taking
   * presses, so an impatient second click cannot patch the same enquiry twice.
   */
  busy?: boolean;
}

/**
 * Moves an enquiry to another stage of the pipeline.
 *
 * One dialog rather than a stage per menu item: the row menu grew an entry for
 * every status it was not already on, which is a list that reads as four
 * different actions when it is one, and says nothing about where the enquiry
 * currently stands.
 *
 * Both screens that offer the move open this same dialog, so the list and the
 * detail page cannot drift apart on what the stages are or how they are set.
 */
const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  name,
  status,
  onClose,
  onSave,
  busy = false,
}) => {
  const [next, setNext] = useState<EnquiryStatus>(status);

  return (
    <Modal
      open
      title="Change status"
      onClose={onClose}
      className="max-w-[440px]"
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="md"
            // Saving the stage it is already on is not a change; the way out
            // of a dialog opened by mistake is Cancel.
            disabled={next === status}
            loading={busy}
            onClick={() => !busy && onSave(next)}
          >
            Save changes
          </Button>
        </>
      }
    >
      <p className="mb-4 text-[14px] 3xl:text-[15px] text-ink-muted">
        Where <span className="font-medium text-ink">{name}</span> stands in
        your pipeline.
      </p>

      <DropdownField
        label="Status"
        value={next}
        options={ENQUIRY_STATUS_OPTIONS}
        // The control opens on the current stage when that is one of the two
        // on offer. An enquiry still at "Recently Added", or already at "Payment
        // sent", opens on the placeholder instead: neither is a stage this
        // dialog can move one to, so showing it as the picked option would
        // offer a choice that is not there. Save stays refused until one of
        // the two is actually picked.
        placeholder="Select status"
        onValueChange={(value) => setNext(value as EnquiryStatus)}
      />
    </Modal>
  );
};

export default ChangeStatusModal;
